import { Component, HostListener, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule, NgFor } from '@angular/common';
import { LangCode, LanguageService } from '../../../core/services/language.service';

type MicrosModel = { slug: string; title: string; views: string[]; };
type Category = { slug: string; name: string; file: string; };

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, NgFor, CommonModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent {
  lang = inject(LanguageService);

  playing = true;

  // ======== Diccionario local (ES/EN/PT/KO) ========
  private dict: Record<LangCode, any> = {
    en: {
      common:{ prev:'Previous', next:'Next', slide:'Slide' },
      hero:{
        top:'Hematology Analyzers',
        typing1:'Alphatec Scientific®',
        typing2:'Precise Results.',
        lead:'Combines analyzers, reagents and cutting-edge medical software',
        actionsLbl:'Main actions',
        ctaExplore:'Explore products', ctaQuote:'Get a quote',
        noSupport:'Your browser does not support embedded video.',
        aria:{ play:'Play video', pause:'Pause video' }
      },
      cats:{
        title:'Explore our lines',
        subtitle:'Explore our diagnostic lines and laboratory solutions.',
        btnViewCats:'See categories', btnQuote:'Request a quote',
        names:{ centrifuges:'Centrifuges', chemistry:'Chemistry', hematology:'Hematology', immunology:'Immunology', microscopes:'Microscopes', radiology:'Radiology', urinalysis:'Urinalysis' }
      },
      about:{
        p1:'Provides in vitro diagnostics solutions, medical IT and hospital equipment to improve healthcare. Our Operations Center in Peru focuses on innovation and developing medical products for people worldwide.',
        p2a:'Focused on optimal diagnosis and treatment,',
        p2b:'we offer top-quality products under ISO 9001, ISO 13485, CE and Marca Perú, meeting diverse needs of medical laboratories and hospital equipment.',
        btnMore:'More information', btnContact:'Contact us'
      },
      series:{
        titleA:'Meet the analyzers', series:'Series M', subtitle:'Built to grow.',
        btnBio:'View Biochemistry', btnQuote:'Request a quote'
      },
      bio:{
        titleA:'Biochemistry Analyzers',
        subtitleA:'More analytes, fewer steps,', subtitleAccent:'better decisions',
        c1:{ title:'ALPSC® 200', desc:'Automatic clinical chemistry analyzer for daily routines.' },
        c2:{ title:'ALPSC® 800', desc:'High capacity, precision and connectivity for growing labs.' },
        btnExplore:'Explore products', btnAllCats:'View all categories'
      },
      micros:{
        line:'Microscope line',
        sub:'Designed for a variety of applications, using LED light sources for high-quality imaging.',
        view:'view', thumb:'thumbnail',
        btnMore:'More information', btnQuote:'Request a quote'
      },
      connect:{
        title:'Connecting results.',
        sub:'modern solutions',
        imgAlt:'App on tablet',
        paragraph:'modern solutions for laboratories and hospitals: flexible architecture, 24/7 support, traceability and reliability. Interfaces for multiple analyzers, automated workflows with barcodes and 100% auditable.',
        btnLis:'Learn about LIS', btnMore:'See more solutions',
        brandsAria:'Brands', brand:'Brand'
      }
    },
    es: {
      common:{ prev:'Anterior', next:'Siguiente', slide:'Slide' },
      hero:{
        top:'Analizadores Hematológicos',
        typing1:'Alphatec Scientific®',
        typing2:'Resultados Precisos.',
        lead:'Combina analizadores, reactivos y software médico de última generación',
        actionsLbl:'Acciones principales',
        ctaExplore:'Explorar productos', ctaQuote:'Cotiza ahora',
        noSupport:'Tu navegador no soporta video embebido.',
        aria:{ play:'Reproducir video', pause:'Pausar video' }
      },
      cats:{
        title:'Explora nuestras líneas',
        subtitle:'Explora nuestras líneas de diagnóstico y soluciones de laboratorio.',
        btnViewCats:'Ver categorías', btnQuote:'Solicitar cotización',
        names:{ centrifuges:'Centrífugas', chemistry:'Bioquímica', hematology:'Hematología', immunology:'Inmunología', microscopes:'Microscopios', radiology:'Radiología', urinalysis:'Uroanálisis' }
      },
      about:{
        p1:'Proporciona soluciones de diagnóstico in vitro, tecnologías de información médica y equipos hospitalarios para la mejora de la atención médica. El Centro de Operaciones, ubicado en Perú, busca innovar y desarrollar productos médicos para la población mundial.',
        p2a:'Centrados en el diagnóstico y el tratamiento óptimo,',
        p2b:'ofrecemos productos de la más alta calidad, basados en las normas ISO 9001, ISO 13485, CE y Marca Perú, para las diversas necesidades de laboratorios médicos y equipos hospitalarios.',
        btnMore:'Más información', btnContact:'Contáctanos'
      },
      series:{
        titleA:'Conoce los analizadores', series:'Serie M', subtitle:'Creados para crecer.',
        btnBio:'Ver Bioquímicos', btnQuote:'Solicitar cotización'
      },
      bio:{
        titleA:'Analizadores Bioquímicos',
        subtitleA:'Más analitos, menos pasos,', subtitleAccent:'mejores decisiones',
        c1:{ title:'ALPSC® 200', desc:'Analizador de química clínica automático para rutinas diarias.' },
        c2:{ title:'ALPSC® 800', desc:'Alta capacidad, precisión y conectividad para laboratorios en crecimiento.' },
        btnExplore:'Explorar productos', btnAllCats:'Ver todas las categorías'
      },
      micros:{
        line:'Línea de microscopios',
        sub:'Diseñada para una variedad de aplicaciones, utilizando fuentes de luz LED para obtener imágenes de calidad.',
        view:'vista', thumb:'miniatura',
        btnMore:'Más información', btnQuote:'Solicitar cotización'
      },
      connect:{
        title:'Conectando resultados.',
        sub:'soluciones modernas',
        imgAlt:'App en tablet',
        paragraph:'soluciones modernas para laboratorios y hospitales: arquitectura flexible, soporte 24/7, trazabilidad y fiabilidad. Interfaces para diversos analizadores, procesos automatizados con código de barras y 100% auditables.',
        btnLis:'Conocer LIS', btnMore:'Ver más soluciones',
        brandsAria:'Marcas', brand:'Marca'
      }
    },
    pt: {
      common:{ prev:'Anterior', next:'Seguinte', slide:'Slide' },
      hero:{
        top:'Analisadores de Hematologia',
        typing1:'Alphatec Scientific®',
        typing2:'Resultados Precisos.',
        lead:'Combina analisadores, reagentes e software médico de última geração',
        actionsLbl:'Ações principais',
        ctaExplore:'Explorar produtos', ctaQuote:'Solicitar orçamento',
        noSupport:'Seu navegador não suporta vídeo incorporado.',
        aria:{ play:'Reproduzir vídeo', pause:'Pausar vídeo' }
      },
      cats:{
        title:'Explore nossas linhas',
        subtitle:'Conheça nossas linhas de diagnóstico e soluções laboratoriais.',
        btnViewCats:'Ver categorias', btnQuote:'Solicitar orçamento',
        names:{ centrifuges:'Centrífugas', chemistry:'Química', hematology:'Hematologia', immunology:'Imunologia', microscopes:'Microscópios', radiology:'Radiologia', urinalysis:'Urinálise' }
      },
      about:{
        p1:'Fornece soluções de diagnóstico in vitro, TI médica e equipamentos hospitalares para melhorar o cuidado da saúde. Nosso Centro de Operações no Peru busca inovar e desenvolver produtos médicos para o mundo.',
        p2a:'Focados no diagnóstico e no tratamento ideal,',
        p2b:'oferecemos produtos de alta qualidade segundo ISO 9001, ISO 13485, CE e Marca Perú, para as diversas necessidades de laboratórios e hospitais.',
        btnMore:'Mais informações', btnContact:'Contate-nos'
      },
      series:{ titleA:'Conheça os analisadores', series:'Série M', subtitle:'Criados para crescer.', btnBio:'Ver Bioquímicos', btnQuote:'Solicitar orçamento' },
      bio:{
        titleA:'Analisadores Bioquímicos',
        subtitleA:'Mais analitos, menos etapas,', subtitleAccent:'melhores decisões',
        c1:{ title:'ALPSC® 200', desc:'Analisador de química clínica automático para rotinas diárias.' },
        c2:{ title:'ALPSC® 800', desc:'Alta capacidade, precisão e conectividade para laboratórios em crescimento.' },
        btnExplore:'Explorar produtos', btnAllCats:'Ver todas as categorias'
      },
      micros:{
        line:'Linha de microscópios',
        sub:'Projetada para várias aplicações, usando LEDs para imagens de qualidade.',
        view:'vista', thumb:'miniatura',
        btnMore:'Mais informações', btnQuote:'Solicitar orçamento'
      },
      connect:{
        title:'Conectando resultados.',
        sub:'soluções modernas',
        imgAlt:'App em tablet',
        paragraph:'soluções modernas para laboratórios e hospitais: arquitetura flexível, suporte 24/7, rastreabilidade e confiabilidade. Interfaces para diversos analisadores, processos automatizados com códigos de barras e 100% auditáveis.',
        btnLis:'Conhecer o LIS', btnMore:'Ver mais soluções',
        brandsAria:'Marcas', brand:'Marca'
      }
    },
    ko: {
      common:{ prev:'이전', next:'다음', slide:'슬라이드' },
      hero:{
        top:'혈액학 분석기',
        typing1:'Alphatec Scientific®',
        typing2:'정확한 결과',
        lead:'첨단 분석기·시약·의료 소프트웨어를 결합합니다',
        actionsLbl:'주요 작업',
        ctaExplore:'제품 살펴보기', ctaQuote:'견적 요청',
        noSupport:'브라우저가 내장 동영상을 지원하지 않습니다.',
        aria:{ play:'동영상 재생', pause:'동영상 일시정지' }
      },
      cats:{
        title:'제품 라인 살펴보기',
        subtitle:'진단 라인과 실험실 솔루션을 확인하세요.',
        btnViewCats:'카테고리 보기', btnQuote:'견적 요청',
        names:{ centrifuges:'원심분리기', chemistry:'화학', hematology:'혈액학', immunology:'면역학', microscopes:'현미경', radiology:'영상의학', urinalysis:'요검사' }
      },
      about:{
        p1:'체외진단 솔루션, 의료 IT, 병원 장비를 제공하여 의료 서비스를 향상합니다. 페루에 위치한 운영센터는 전 세계를 위한 의료 제품 혁신과 개발에 주력합니다.',
        p2a:'최적의 진단과 치료에 집중하여',
        p2b:'ISO 9001, ISO 13485, CE, Marca Perú 기준의 최고 품질 제품을 제공합니다.',
        btnMore:'자세히 보기', btnContact:'문의하기'
      },
      series:{ titleA:'분석기 소개', series:'M 시리즈', subtitle:'성장을 위해 설계됨.', btnBio:'생화학 보기', btnQuote:'견적 요청' },
      bio:{
        titleA:'생화학 분석기',
        subtitleA:'더 많은 분석물, 더 적은 단계,', subtitleAccent:'더 나은 결정',
        c1:{ title:'ALPSC® 200', desc:'일상 검사에 적합한 자동 임상화학 분석기.' },
        c2:{ title:'ALPSC® 800', desc:'성장하는 연구실을 위한 고용량·고정밀·연결성.' },
        btnExplore:'제품 살펴보기', btnAllCats:'모든 카테고리'
      },
      micros:{
        line:'현미경 라인',
        sub:'다양한 용도에 맞게 설계되었으며, LED 광원을 사용해 고품질 이미지를 제공합니다.',
        view:'뷰', thumb:'썸네일',
        btnMore:'자세히 보기', btnQuote:'견적 요청'
      },
      connect:{
        title:'결과를 연결합니다.',
        sub:'모던 솔루션',
        imgAlt:'태블릿의 앱',
        paragraph:'실험실과 병원을 위한 모던 솔루션: 유연한 아키텍처, 24/7 지원, 추적성과 신뢰성. 다양한 분석기와의 인터페이스, 바코드 기반 자동화, 100% 감사 가능.',
        btnLis:'LIS 알아보기', btnMore:'더 많은 솔루션',
        brandsAria:'브랜드', brand:'브랜드'
      }
    }
  };

  // ======= Datos que dependen del idioma =======
  // Categorías base (slug/archivo fijos)
  private baseCategories: Omit<Category, 'name'>[] = [
    { slug: 'centrifuges', file: 'centrifuges.png' },
    { slug: 'chemistry',   file: 'chemistry.png' },
    { slug: 'hematology',  file: 'hematology.png' },
    { slug: 'immunology',  file: 'immunology.png' },
    { slug: 'microscopes', file: 'microscopes.png' },
    { slug: 'radiology',   file: 'radiology.png' },
    { slug: 'urinalysis',  file: 'urinalysis.png' },
  ];

  categories: Category[] = [];
  get categoriesLoop() { return [...this.categories, ...this.categories]; }
  trackByCategory = (index: number, item: { slug: string }) => `${item.slug}-${index}`;

  get categorySlides() {
    const size = 5;
    const out: Category[][] = [];
    for (let i = 0; i < this.categories.length; i += size) {
      out.push(this.categories.slice(i, i + size));
    }
    if (out.length) {
      const last = out[out.length - 1];
      while (last.length < size) last.push(this.categories[last.length % this.categories.length]);
    }
    return out;
  }

  // Acordeón LIS (se reconstruye por idioma)
  lisItems: Array<{ title:string; icon:string; description:string }> = [];

  // Serie M (imágenes fijas)
  serieMSlides = [
    { src: 'assets/images/Alpsc200.png',   alt: 'ALPSC 200' },
    { src: 'assets/images/Alpsc200fd.png', alt: 'ALPSC 200 FD' },
    { src: 'assets/images/Alpsc800f.png',  alt: 'ALPSC 800 F' },
    { src: 'assets/images/Alpsc1600.png',  alt: 'ALPSC 1600' },
  ];

  // Marcas (imágenes fijas)
  images = [
    'assets/brand/brand_1.png',
    'assets/brand/brand_2.png',
    'assets/brand/brand_3.png',
    'assets/brand/brand_4.png',
    'assets/brand/brand_5.png',
  ];

  // Microscopios (nombres de producto se mantienen)
  microsModels: MicrosModel[] = [
    { slug:'astre1',  title:'ASTREO® 100',    views:['Astreo 100 ESDE.png','Astreo 100 ESIZ.png','Astreo 100 F.png','Astreo 100 LD.png'] },
    { slug:'astre3',  title:'ASTREO® 300',    views:['Astreo 300 ESDE.png','Astreo 300 ESIZ.png','Astreo 300 F.png','Astreo 300LD.png'] },
    { slug:'astre3p', title:'ASTREO® 300plus',views:['Astreo 300plus ESDE.png','Astreo 300plus ESIZ.png','Astreo 300plus F.png','Astreo 300plusLD.png'] },
    { slug:'astre6',  title:'ASTREO® 620',    views:['Astreo 620 ESDE.png','Astreo 620ESIZ.png','Astreo 620 F.png','Astreo 620 LD.png'] },
  ];
  selectedViewIndex: Record<string, number> = { astre1:0, astre3:0, astre3p:0, astre6:0 };
  imgPath(f: string){ return `assets/images/microscopios/${f}`; }
  selectView(slug: string, idx:number){ this.selectedViewIndex[slug] = idx; }
  trackBySlug(_: number, m: MicrosModel){ return m.slug; }

  constructor(){
    this.rebuild();
    this.lang.lang$.subscribe(() => this.rebuild());
  }

  // ===== Helpers =====
  t(path: string): string {
    const obj = this.dict[this.lang.lang];
    return path.split('.').reduce<any>((acc, k) => acc?.[k], obj) ?? path;
  }

  private rebuild(){
    // Categorías
    const names = this.dict[this.lang.lang].cats.names;
    this.categories = this.baseCategories.map(b => ({
      ...b,
      name: names[b.slug as keyof typeof names] || b.slug
    }));

    // LIS items
    const items = {
      es: [
        { t:'Registro de atención', i:'bi-person-vcard', d:'Incluye la identificación y clasificación del paciente y los exámenes clínicos. Gestión de códigos de barras para la identificación y trazabilidad de las muestras, así como para la integración con los analizadores.' },
        { t:'Recolección y verificación de muestras', i:'bi-clipboard2-check', d:'Incluye fecha, hora y responsable, así como la razón de rechazo y las acciones correctivas.' },
        { t:'Emisión de hojas de trabajo', i:'bi-file-earmark-spreadsheet', d:'Por área de prueba pendientes de registro para facilitar el procesamiento por lotes y el ingreso de resultados.' },
        { t:'Registro y validación de resultados', i:'bi-shield-check', d:'Alertas de resultados patológicos o de emergencia, rangos de referencia y consultas históricas para la validación.' },
        { t:'Impresión de historial de resultados', i:'bi-printer', d:'Historial gráfico y columnar de resultados por periodo.' }
      ],
      en: [
        { t:'Patient intake', i:'bi-person-vcard', d:'Patient ID and triage with clinical tests. Barcode management for sample identification and traceability and analyzer integration.' },
        { t:'Sample collection & verification', i:'bi-clipboard2-check', d:'Date, time and responsible person, plus rejection reasons and corrective actions.' },
        { t:'Worklist generation', i:'bi-file-earmark-spreadsheet', d:'Per test area, to enable batch processing and result entry.' },
        { t:'Result entry & validation', i:'bi-shield-check', d:'Alerts for critical/pathological results, reference ranges and historical queries to support validation.' },
        { t:'Result history printing', i:'bi-printer', d:'Graph history and columnar history of results for a given period.' }
      ],
      pt: [
        { t:'Registro de atendimento', i:'bi-person-vcard', d:'Identificação e triagem do paciente. Códigos de barras para amostras e integração com analisadores.' },
        { t:'Coleta e verificação de amostras', i:'bi-clipboard2-check', d:'Data, hora e responsável, além de motivo de rejeição e ações corretivas.' },
        { t:'Emissão de folhas de trabalho', i:'bi-file-earmark-spreadsheet', d:'Por área de teste para processamento em lote e lançamento de resultados.' },
        { t:'Registro e validação de resultados', i:'bi-shield-check', d:'Alertas, faixas de referência e histórico para apoiar a validação.' },
        { t:'Impressão do histórico de resultados', i:'bi-printer', d:'Histórico gráfico e em colunas por período.' }
      ],
      ko: [
        { t:'접수/등록', i:'bi-person-vcard', d:'환자 식별 및 분류, 임상 검사. 바코드로 시료 추적 및 분석기 연동.' },
        { t:'시료 채취·검증', i:'bi-clipboard2-check', d:'일시와 담당자, 거부 사유 및 시정 조치 기록.' },
        { t:'워크리스트 발행', i:'bi-file-earmark-spreadsheet', d:'검사 영역별 배치 처리와 결과 입력 지원.' },
        { t:'결과 입력/검증', i:'bi-shield-check', d:'위험/병리 알림, 기준 범위, 이력 조회로 검증 지원.' },
        { t:'결과 이력 출력', i:'bi-printer', d:'기간별 그래프 및 표 형식 이력.' }
      ]
    } as const;

    const key = this.lang.lang;
    const src = key === 'es' ? items.es : key === 'pt' ? items.pt : key === 'ko' ? items.ko : items.en;
    this.lisItems = src.map(x => ({ title:x.t, icon:x.i, description:x.d }));
  }

  // ===== Carrusel de cartas (lo que ya tenías) =====
  selected = 2;
  hover = -1;
  select(i: number){ this.selected = i; }
  getTransform(i: number){
    const offset = i - this.selected;
    const baseScale = i === this.selected ? 1.06 : 0.88;
    const hoverBoost = this.hover === i ? 0.04 : 0;
    const scale = baseScale + hoverBoost;
    const rotate = Math.max(-10, Math.min(10, offset * 4));
    return `translate(-50%, -50%) translateX(calc(${offset} * var(--gap))) rotate(${rotate}deg) scale(${scale})`;
  }
  getZ(i: number){ const d = 100 - Math.abs(i - this.selected); return this.hover === i ? d + 2 : d; }
  onKey(i:number, ev:KeyboardEvent){ if (ev.key==='Enter'||ev.key===' '){ ev.preventDefault(); this.select(i); } }
  @HostListener('window:resize') onResize(){}

  toggleVideo(vid: HTMLVideoElement){
    if (vid.paused){ vid.play(); this.playing = true; }
    else { vid.pause(); this.playing = false; }
  }
}
